'use client';

import Link from 'next/link';
import { useLocale } from '@/lib/i18n/LocaleContext';
import type { LocalizedJobPosting } from '@/lib/jobs';

export function CareersIndexClient({ jobs }: { jobs: LocalizedJobPosting[] }) {
  const { dict, href } = useLocale();

  return (
    <div className="self-styled">
      {/* Hero */}
      <section
        className="relative flex min-h-[60vh] flex-col items-center justify-center text-center"
        style={{ backgroundColor: '#173835', paddingTop: '140px', paddingBottom: '80px' }}
      >
        <span className="eyebrow" style={{ marginBottom: '24px' }}>{dict.careers.index.eyebrow}</span>
        <h1 className="rtl-display-l font-six-caps text-[calc(1rem+14vw)] leading-[0.85] text-white lowercase content-padding">
          {dict.careers.index.heading}
        </h1>
        <p
          className="max-w-2xl px-8 text-center text-lg leading-relaxed text-white/60 md:text-xl"
          style={{ marginTop: '40px' }}
        >
          {dict.careers.index.subtitle}
        </p>
      </section>

      {/* Job list */}
      <section style={{ backgroundColor: '#0c0c0c', paddingTop: '100px', paddingBottom: '150px' }}>
        <div className="content-padding" style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {jobs.length === 0 ? (
            <p className="text-white/50 text-center">{dict.careers.index.empty}</p>
          ) : (
            <div className="flex flex-col" style={{ gap: '24px' }}>
              {jobs.map((job) => (
                <Link
                  key={job.slug}
                  href={href(`/careers/${job.slug}`)}
                  className="group flex flex-col md:flex-row md:items-center md:justify-between rounded-xl border border-white/10 bg-white/[0.03] transition-colors hover:border-emerald-400/40 hover:bg-white/[0.05]"
                  style={{ padding: '36px 40px', gap: '16px' }}
                >
                  <div>
                    <span className="text-xs uppercase tracking-[0.28em] text-emerald-400/80 font-mono">
                      {job.department} · {job.location}
                    </span>
                    <h2 className="text-white text-2xl md:text-3xl font-medium" style={{ marginTop: '8px' }}>
                      {job.title}
                    </h2>
                    <p className="text-white/50 text-sm" style={{ marginTop: '8px', maxWidth: '520px' }}>
                      {job.summary}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    {job.openings > 1 && (
                      <span className="text-xs uppercase tracking-[0.2em] text-white/40 font-mono">
                        <bdi dir="ltr">{job.openings}</bdi> {dict.careers.job.openings}
                      </span>
                    )}
                    <span className="inline-flex items-center justify-center rounded-full border border-white/30 text-sm text-white transition-colors group-hover:border-white group-hover:bg-white group-hover:text-black" style={{ padding: '12px 28px', gap: '8px' }}>
                      {dict.careers.job.viewRole} <span className="rtl-mirror" aria-hidden="true">→</span>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
